<script lang="ts">
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import {
		addDollarSign,
		getDecimalOfToken,
		getNumBackInUnits,
		getPrecisionOfToken,
		sanitizeInputs,
		toBN,
		trimString
	} from '$lib/util/util';
	import { scale } from 'svelte/transition';
	import { quintOut } from 'svelte/easing';
	import {
		addFundsToken,
		addFundsAmount,
		addFundsInsufficientBalance,
		addFundsTokenAmount,
		processingFundDeposit,
		smartContractWalletAddress,
		smartWalletName
	} from '$lib/home/scwallet/store';
	import { nativeBalancesEnriched, userBalancesEnriched } from '$lib/home/store';
	import { popupModalOpen } from '../popupsStore';
	import { addFundsToSCW } from '$lib/web3/actions/scwModifiers';
	import type { SendTransactionResult } from '@wagmi/core';
	import { optimismTokensConfig } from '$lib/tokensConfig';
	import {
		addErrorNotification,
		addTransactionNotification
	} from '$lib/notifications/notificationManager';
	import BigNumber from 'bignumber.js';
	import ScButton from '$lib/home/scwallet/utils/SCButton.svelte';
	import { getUserBalances } from '$lib/data/main';
	import { getParsedError } from '$lib/web3/errorHandling';
	import ScTokenSelector from '$lib/home/scwallet/utils/SCTokenSelector.svelte';
	import ScTokenDropDown from '$lib/home/scwallet/utils/SCTokenDropDown.svelte';
	import type { TokenSymbol } from '$lib/types/tokens';
	import { tokensAllowedForDeposit } from '$lib/home/scwallet/config';

	export let title = '';
	export let title2 = '';
	export let minDepositNeeded: string = '0';

	let depositInput: string = '';
	let isContentEditable = false;
	let smartWalletNameInput: string = $smartWalletName;
	let showTokenSelector: boolean = false;

	function setMaxAddInput() {
		$addFundsTokenAmount = toBN(
			new BigNumber($nativeBalancesEnriched[$addFundsToken].balance).div(1e18).toString()
		);
		depositInput = getNumBackInUnits(
			$addFundsTokenAmount.toString(),
			getDecimalOfToken($addFundsToken),
			getPrecisionOfToken($addFundsToken)
		);
	}
	function depositInputChanged() {
		depositInput = sanitizeInputs(depositInput);
		if (depositInput) $addFundsTokenAmount = toBN(depositInput, getDecimalOfToken($addFundsToken));
		else $addFundsTokenAmount = toBN('0');
	}

	const toggleEditWalletName = () => {
		isContentEditable = !isContentEditable;
	};

	const smartWalletNameChanged = () => {
		$smartWalletName = smartWalletNameInput;
		window.localStorage.setItem(`walletName-${$smartContractWalletAddress}`, smartWalletNameInput);
	};

	const closeModal = () => {
		popupModalOpen.set(false);
	};

	async function executeAddFunds() {
		try {
			$processingFundDeposit = true;
			let tx: SendTransactionResult = await addFundsToSCW(
				optimismTokensConfig[$addFundsToken].address as `0x${string}`,
				$smartContractWalletAddress as `0x${string}`,
				$addFundsTokenAmount
			);
			addTransactionNotification(
				'Funds Deposited',
				`${depositInput} ${$addFundsToken} have been transfered from Polynomial wallet to your wallet.`,
				tx,
				() => {
					(depositInput = '0'), ($addFundsTokenAmount = toBN('0'));
					getUserBalances();
					$processingFundDeposit = false;
				}
			);
		} catch (e: any) {
			const { title, description } = getParsedError(e);
			addErrorNotification(title, description, JSON.stringify(e));
			$processingFundDeposit = false;
		}
	}

	function changeToken(token: TokenSymbol) {
		$addFundsToken = token;
		showTokenSelector = false;
		$addFundsTokenAmount = toBN('0');
		depositInput = '';
	}
</script>

<GradientBorder
	gradient="bg-fx-gradient-6 "
	paddingClass="p-px"
	borderRadius="rounded-2xl squircle"
	innerClassList="bg-fx-gradient-8 "
>
	<div class="flex flex-col justify-center items-center p-8">
		<div class="flex items-start justify-between w-full">
			<img class="invisible" loading="lazy" src={'scwallet/cross-small.svg'} alt="" />
			<img loading="lazy" src={'scwallet/stary-check-mark.svg'} alt="" />
			<button on:keydown={closeModal} on:click={closeModal}>
				<img class="" loading="lazy" src={'scwallet/cross-small.svg'} alt="" />
			</button>
		</div>
		<div class="text-white text-xl text-center font-demibold text-fx-gradient-5 font-semibold">
			{title} <br />
			{title2}
		</div>
		<div class="">
			<div class=" justify-center h-44">
				<div in:scale={{ duration: 250, delay: 0, opacity: 0.5, start: 0.9, easing: quintOut }}>
					<img
						loading="lazy"
						src={'scwallet/succes-shield-1.webp'}
						alt=""
						class="animate-[pulse_3s_ease-in-out_infinite] w-52"
					/>
				</div>
				<div class="flex items-center justify-center -mt-20">
					<GradientBorder
						gradient="bg-fx-gradient-14"
						paddingClass="p-px"
						borderRadius="rounded-lg squircle-lg"
						outerClassList=""
						innerClassList="flex justify-center items-center bg-fx-black "
					>
						<div class="bg-fx-gradient-6 text-fx-white py-1 px-1 w-44 flex justify-between">
							<div class="text-base">Balance</div>
							<div class="text-base text-fx-gradient-22">
								{#if $smartContractWalletAddress && $nativeBalancesEnriched && $nativeBalancesEnriched[$addFundsToken]}
									{getNumBackInUnits(
										$userBalancesEnriched[$addFundsToken]?.balance,
										getDecimalOfToken($addFundsToken),
										2
									)}
								{:else}
									<div
										class="animate-pulse bg-fx-grey-600 h-5 col-span-2 rounded-lg squircle-lg w-10 mx-auto"
									/>
								{/if}
							</div>
							<div class="flex items-center gap-1">
								<img
									loading="lazy"
									src={optimismTokensConfig[$addFundsToken].logo}
									alt="menu"
									class="w-4 h-4"
								/>
								<div class="text-fx-grey-200 text-sm font-medium">{$addFundsToken}</div>
							</div>
						</div>
					</GradientBorder>
				</div>
			</div>
		</div>

		<div class="border border-fx-grey-800 w-full mb-6" />
		<div class="text-fx-grey-400 font-normal text-xs mb-3">
			To start using Polynomial, you need to deposit in it to place trade.
		</div>

		<div class="flex justify-between items-center w-full mb-2">
			<div class="text-fx-grey-100 text-base font-semibold">Deposit sUSD in your</div>
			<GradientBorder
				gradient="bg-fx-gradient-10"
				paddingClass="p-px"
				borderRadius="rounded-sm squircle"
				innerClassList="bg-fx-gradient-8 "
			>
				<div class="bg-fx-gradient-6 flex items-center px-1 justify-between">
					<img class="h-6" loading="lazy" src={'scwallet/shield.svg'} alt="" />
					<div class="text-fx-grey-200 text-xs">
						<div class="flex items-center gap-1">
							{#if isContentEditable}
								<input
									autocomplete="off"
									autocorrect="off"
									minlength="1"
									maxlength="30"
									spellcheck="false"
									bind:value={smartWalletNameInput}
									on:input={smartWalletNameChanged}
									on:blur={toggleEditWalletName}
									type="string"
									placeholder="Wallet name"
									class="bg-transparent text-fx-white outline-none w-24 text-center"
								/>
							{:else}
								<div>
									{trimString($smartWalletName, 15)}
								</div>
								<button on:click={toggleEditWalletName} on:keydown={toggleEditWalletName}>
									<img loading="lazy" src={'edit.svg'} alt="help" class="w-5 h-5" />
								</button>
							{/if}
						</div>
					</div>
				</div>
			</GradientBorder>
		</div>

		<div class="border rounded-xl border-fx-grey-500 w-full mb-6 relative">
			<ScTokenDropDown {changeToken} {showTokenSelector} tokensAllowed={$tokensAllowedForDeposit} />
			<div class="flex justify-between items-center m-4">
				<p class="text-fx-white text-2xl">
					<input
						inputmode="decimal"
						autocomplete="off"
						autocorrect="off"
						minlength="1"
						maxlength="79"
						spellcheck="false"
						pattern="^[0-9]*[.,]?[0-9]*$"
						bind:value={depositInput}
						on:input={depositInputChanged}
						type="text"
						placeholder="0"
						class="bg-transparent text-fx-white font-medium outline-none text-xl max-w-[200px]"
					/>
				</p>
				<ScTokenSelector bind:showTokenSelector token={$addFundsToken} />
			</div>
			<div class="flex justify-between items-center m-4">
				<p class="text-fx-grey-400 text-xs">
					{#if depositInput}
						<div class="text-fx-grey-400 text-xs">
							{addDollarSign(getNumBackInUnits($addFundsAmount.toString(), 18, 2))}
						</div>
					{:else}
						<div class="text-fx-grey-400 text-xs">-</div>
					{/if}
				</p>
				<div>
					<div class="text-fx-grey-400 text-xs ml-auto mr-2 flex gap-1.5">
						Balance:
						{#if $nativeBalancesEnriched && $nativeBalancesEnriched[$addFundsToken]}
							<span class="text-fx-grey-200 text-xs"
								>{getNumBackInUnits(
									$nativeBalancesEnriched[$addFundsToken]?.balance,
									getDecimalOfToken($addFundsToken),
									getPrecisionOfToken($addFundsToken)
								)}</span
							>
							{#if depositInput !== getNumBackInUnits($nativeBalancesEnriched[$addFundsToken]?.balance, getDecimalOfToken($addFundsToken), getPrecisionOfToken($addFundsToken))}
								<button
									class="cursor-pointer text-xs text-fx-blue underline"
									on:click={setMaxAddInput}
									on:keydown={setMaxAddInput}
								>
									Max
								</button>
							{/if}
						{:else}
							<span class="text-fx-grey-200 text-xs">--</span>
						{/if}
					</div>
				</div>
			</div>
		</div>
		<div class="w-full mb-4">
			<ScButton
				input={depositInput}
				fundsAmount={$addFundsAmount}
				isInsufficientBalance={$addFundsInsufficientBalance}
				processing={$processingFundDeposit}
				execute={executeAddFunds}
				type={'Add'}
				{minDepositNeeded}
			/>
		</div>
	</div>
</GradientBorder>
